/**
 * HeaderToolbar Component
 * Main transport bar header containing project name, transport controls,
 * editing tools, and action buttons
 */

import React from 'react';
import { TapTempoControl } from '../../TapTempo';
import { RestartTourButton } from '../../QuickTour';
import { RecentProjectsButton } from '../../RecentProjects';
import { SimplifiedModeToggle } from '../simplifiedmode';
import { ViewTabs } from '../mixer';
import {
  ProjectHeader,
  MainTransportButtons,
  MetronomeToggle,
  LoopToggle,
  InputMonitorToggle,
  AudioInputButton,
  EditingTools,
  GridSnapToggle,
  AutomationToggle,
  InstrumentsToggle,
  MIDIButtonWithPanel,
  UndoRedoButtons,
  SmartTempoToggle,
  ChordTrackToggle,
  ChordWizardButton,
  EffectsToggle,
  MasteringToggle,
  PluginRackToggle,
  QuickStartButton,
  AIMixingToggle,
  AIMasteringToggle,
  CollaborationButton,
  JamSessionButton,
  AIMixAssistantButton,
  AIChordGeneratorButton,
  AILyricAssistantButton,
  AIDrumGeneratorButton,
  VideoSyncButton,
  SampleLibraryButton,
  PluginBrowserButton,
  PluginMarketplaceButton,
  KeyboardShortcutsButton,
  ThemeToggleButton,
  DuplicateProjectButton,
  UserMenu,
  ProjectControls,
  ExportButtons,
} from './index';
import { Button } from '../../ui/button';
import { Save, Loader2, FolderOpen, Wand2, Settings2 } from 'lucide-react';

export const HeaderToolbar = ({
  // Project
  projectName,
  setProjectName,
  projectId,
  
  // Transport
  isPlaying,
  currentTime,
  playAll,
  stopAll,
  seekTo,
  setCurrentTime,
  showCollaboration,
  formatTime,
  
  // BPM
  bpm,
  setBpm,
  pushUndoAction,
  ActionTypes,
  
  // Metronome
  metronomeEnabled,
  setMetronomeEnabled,
  metronomeVolume,
  setMetronomeVolume,
  metronomeGainRef,
  currentBeat,
  
  // Loop
  loopEnabled,
  setLoopEnabled,
  loopStart,
  loopEnd,
  
  // Input Monitor
  inputMonitorEnabled,
  setInputMonitorEnabled,
  isRecording,
  inputLevel,
  
  // Audio Input Device
  selectedAudioInput,
  setSelectedAudioInput,
  inputGain,
  setInputGain,
  
  // Editing
  clipEditMode,
  setClipEditMode,
  selectedClipsForJoin,
  setSelectedClipsForJoin,
  showMarkersPanel,
  setShowMarkersPanel,
  markersCount,
  onJoinClips,
  
  // Grid Snap
  snapEnabled,
  setSnapEnabled,
  
  // Automation
  showAutomation,
  setShowAutomation,
  
  // Instruments
  showInstruments,
  setShowInstruments,
  isMidiRecording,
  
  // MIDI
  midiDevices,
  showMIDIPanel,
  setShowMIDIPanel,
  midiActivity,
  
  // View Mode
  viewMode,
  setViewMode,
  
  // Undo/Redo
  canUndo,
  canRedo,
  undo,
  redo,
  
  // Panels
  showSmartTempo,
  setShowSmartTempo,
  showChordTrack,
  setShowChordTrack,
  setShowChordWizard,
  showEffects,
  setShowEffects,
  showMastering,
  setShowMastering,
  showPluginRack,
  setShowPluginRack,
  setShowQuickStart,
  showAIMixing,
  setShowAIMixing,
  showAIMastering,
  setShowAIMastering,
  audioCharacteristics,
  collabSessionId,
  setShowSessionManager,
  setShowCollaboration,
  activeJamSession,
  showJamSessionPanel,
  setShowJamSessionPanel,
  setShowJamSessionModal,
  setShowAIMixAssistant,
  setShowAIChordGenerator,
  setShowAILyricAssistant,
  setShowAIDrumGenerator,
  setShowVideoSync,
  setShowSampleLibrary,
  setShowPluginBrowser,
  setShowPluginMarketplace,
  setShowKeyboardShortcuts,
  
  // Simplified Mode
  isSimplifiedMode,
  setIsSimplifiedMode,
  
  // Theme
  isDark,
  toggleTheme,
  
  // Tour
  startTour,
  
  // Navigation
  navigate,
  
  // Template
  setShowTemplateSelector,
  
  // Save/Export
  saveProject,
  isSaving,
  duplicateProject,
  isExporting,
  onExportWav,
  onExportMp3,
  setShowBatchExporter,
  canExportMp3,
  
  // Quick Mix
  onQuickMix,
  isQuickMixing,
  onOpenQuickMixPro,
  hasProPreferences,
  
  // User
  user,
  planId,
  logout,
}) => {
  return (
    <header className="bg-zinc-900/70 backdrop-blur-md border-b border-zinc-800/50 px-3 md:px-4 py-2 flex-shrink-0">
      <div className="flex items-center justify-between gap-2">
        {/* Left: Logo, Project Name, and CRITICAL Recording Controls */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <ProjectHeader
            projectName={projectName}
            setProjectName={setProjectName}
          />
          
          {/* Audio Input & Monitor - ALWAYS VISIBLE */}
          <div className="flex items-center gap-1 ml-2 px-2 py-1 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
            <AudioInputButton
              selectedDevice={selectedAudioInput}
              setSelectedDevice={setSelectedAudioInput}
              inputGain={inputGain}
              setInputGain={setInputGain}
            />
            <InputMonitorToggle
              inputMonitorEnabled={inputMonitorEnabled}
              setInputMonitorEnabled={setInputMonitorEnabled}
              isRecording={isRecording}
              inputLevel={inputLevel}
            />
          </div>
        </div>
        
        {/* Center: Transport Controls - Scrollable */}
        <div className="flex items-center gap-1 md:gap-2 overflow-x-auto scrollbar-hide flex-1 justify-center">
          <MainTransportButtons
            isPlaying={isPlaying}
            currentTime={currentTime}
            playAll={playAll}
            stopAll={stopAll}
            seekTo={seekTo}
            setCurrentTime={setCurrentTime}
            showCollaboration={showCollaboration}
            formatTime={formatTime}
          />
          
          {/* BPM with Tap Tempo - Hidden on mobile */}
          <div className="hidden md:block">
            <TapTempoControl
              bpm={bpm}
              onBpmChange={(newBpm) => {
                pushUndoAction({
                  type: ActionTypes.SET_BPM,
                  oldValue: bpm,
                  newValue: newBpm,
                });
                setBpm(newBpm);
                // Broadcast BPM change to collaborators
                if (showCollaboration && window.__collabSendBpmChange) {
                  window.__collabSendBpmChange(newBpm);
                }
              }}
              compact
            />
          </div>
          
          {/* Metronome Toggle with Volume Control */}
          <MetronomeToggle
            metronomeEnabled={metronomeEnabled}
            setMetronomeEnabled={setMetronomeEnabled}
            metronomeVolume={metronomeVolume}
            setMetronomeVolume={setMetronomeVolume}
            metronomeGainRef={metronomeGainRef}
            currentBeat={currentBeat}
          />
          
          {/* Loop Toggle */}
          <LoopToggle
            loopEnabled={loopEnabled}
            setLoopEnabled={setLoopEnabled}
            loopStart={loopStart}
            loopEnd={loopEnd}
          />
          
          {/* Editing Tools - Split, Markers, Join */}
          <EditingTools
            clipEditMode={clipEditMode}
            setClipEditMode={setClipEditMode}
            selectedClipsForJoin={selectedClipsForJoin}
            setSelectedClipsForJoin={setSelectedClipsForJoin}
            showMarkersPanel={showMarkersPanel}
            setShowMarkersPanel={setShowMarkersPanel}
            markersCount={markersCount}
            onJoinClips={onJoinClips}
          />
          
          {/* Grid Snap Toggle */}
          <GridSnapToggle
            snapEnabled={snapEnabled}
            setSnapEnabled={setSnapEnabled}
          />
          
          {/* Automation Toggle */}
          <AutomationToggle
            showAutomation={showAutomation}
            setShowAutomation={setShowAutomation}
          />
          
          {/* Instruments Toggle */}
          <InstrumentsToggle
            showInstruments={showInstruments}
            setShowInstruments={setShowInstruments}
            isMidiRecording={isMidiRecording}
          />
          
          {/* External MIDI Toggle */}
          <MIDIButtonWithPanel
            midiDevices={midiDevices}
            showMIDIPanel={showMIDIPanel}
            setShowMIDIPanel={setShowMIDIPanel}
            midiActivity={midiActivity}
          />
          
          {/* View Mode Tabs - Pro Tools Style */}
          <div className="hidden md:block ml-2">
            <ViewTabs 
              activeView={viewMode} 
              onViewChange={setViewMode}
            />
          </div>
        </div>
        
        {/* Right: Actions - Scrollable on small screens */}
        <div className="flex items-center gap-1 md:gap-2 overflow-x-auto scrollbar-hide flex-shrink-0 max-w-[60vw] md:max-w-none" data-testid="toolbar-actions-panel">
          {/* Undo/Redo Buttons */}
          <UndoRedoButtons
            canUndo={canUndo}
            canRedo={canRedo}
            undo={undo}
            redo={redo}
          />
          
          <div className="w-px h-6 bg-zinc-800 hidden md:block" />
          
          {/* Smart Tempo Button - Hidden in simplified mode */}
          <SmartTempoToggle
            showSmartTempo={showSmartTempo}
            setShowSmartTempo={setShowSmartTempo}
            isSimplifiedMode={isSimplifiedMode}
          />
          
          {/* Chord Track Toggle - Hidden in simplified mode */}
          <ChordTrackToggle
            showChordTrack={showChordTrack}
            setShowChordTrack={setShowChordTrack}
            isSimplifiedMode={isSimplifiedMode}
          />
          
          {/* Chord Wizard Button */}
          <ChordWizardButton
            setShowChordWizard={setShowChordWizard}
          />
          
          <EffectsToggle
            showEffects={showEffects}
            setShowEffects={setShowEffects}
          />
          
          {/* Mastering Button */}
          <MasteringToggle
            showMastering={showMastering}
            setShowMastering={setShowMastering}
            isSimplifiedMode={isSimplifiedMode}
          />
          
          {/* Plugin Rack Button */}
          <PluginRackToggle
            showPluginRack={showPluginRack}
            setShowPluginRack={setShowPluginRack}
            isSimplifiedMode={isSimplifiedMode}
          />
          
          {/* Quick Start Templates Button */}
          <QuickStartButton
            setShowQuickStart={setShowQuickStart}
          />
          
          {/* AI Mixing Assistant Button */}
          <AIMixingToggle
            showAIMixing={showAIMixing}
            setShowAIMixing={setShowAIMixing}
            isSimplifiedMode={isSimplifiedMode}
          />
          
          {/* AI Mastering Suggestions Button */}
          <AIMasteringToggle
            showAIMastering={showAIMastering}
            setShowAIMastering={setShowAIMastering}
            isSimplifiedMode={isSimplifiedMode}
            audioCharacteristics={audioCharacteristics}
            isPlaying={isPlaying}
          />
          
          {/* Live Collaboration Button */}
          <CollaborationButton
            showCollaboration={showCollaboration}
            setShowCollaboration={setShowCollaboration}
            collabSessionId={collabSessionId}
            setShowSessionManager={setShowSessionManager}
          />
          
          {/* Jam Sessions Button */}
          <JamSessionButton
            activeJamSession={activeJamSession}
            showJamSessionPanel={showJamSessionPanel}
            setShowJamSessionPanel={setShowJamSessionPanel}
            setShowJamSessionModal={setShowJamSessionModal}
          />
          
          {/* AI Mix Assistant Button */}
          <AIMixAssistantButton setShowAIMixAssistant={setShowAIMixAssistant} />
          
          {/* AI Chord Generator Button */}
          <AIChordGeneratorButton setShowAIChordGenerator={setShowAIChordGenerator} />
          
          {/* AI Lyric Assistant Button */}
          <AILyricAssistantButton setShowAILyricAssistant={setShowAILyricAssistant} />
          
          {/* AI Drum Pattern Generator Button */}
          <AIDrumGeneratorButton setShowAIDrumGenerator={setShowAIDrumGenerator} />
          
          {/* Separator */}
          <div className="w-px h-6 bg-zinc-700 mx-1" />
          
          {/* Video Sync Button */}
          <VideoSyncButton setShowVideoSync={setShowVideoSync} />
          
          {/* Sample Library Button */}
          <SampleLibraryButton setShowSampleLibrary={setShowSampleLibrary} />
          
          {/* Plugin Browser Button */}
          <PluginBrowserButton setShowPluginBrowser={setShowPluginBrowser} />
          
          {/* Plugin Marketplace Button */}
          <PluginMarketplaceButton setShowPluginMarketplace={setShowPluginMarketplace} />
          
          {/* Simplified Mode Toggle */}
          {!isSimplifiedMode && (
            <SimplifiedModeToggle
              isSimplified={isSimplifiedMode}
              onToggle={() => setIsSimplifiedMode(!isSimplifiedMode)}
            />
          )}
          
          {/* Keyboard Shortcuts Button */}
          <KeyboardShortcutsButton setShowKeyboardShortcuts={setShowKeyboardShortcuts} />
          
          {/* Theme Toggle Button */}
          <ThemeToggleButton isDark={isDark} toggleTheme={toggleTheme} />
          
          {/* Tour Button */}
          <RestartTourButton className="hidden md:flex" onStartTour={startTour} />
          
          {/* Recent Projects Button */}
          <RecentProjectsButton
            onSelectProject={(id) => {
              navigate(`/studio/${id}`);
            }}
            currentProjectId={projectId}
          />
          
          <ProjectControls
            onSave={saveProject}
            onOpenTemplates={() => setShowTemplateSelector(true)}
            isSaving={isSaving}
          />
          
          {/* Quick Mix - One-click AI mixing */}
          <div className="flex items-center" data-testid="quick-mix-group">
            <Button
              variant="outline"
              size="sm"
              onClick={onQuickMix}
              disabled={isQuickMixing}
              className={`border-purple-500/40 bg-purple-500/10 text-purple-400 hover:bg-purple-500/25 hover:text-purple-200 px-3 font-semibold rounded-r-none ${hasProPreferences ? 'border-r-0' : ''}`}
              data-testid="quick-mix-btn"
              title="One-click AI Mix — analyzes and auto-adjusts all tracks"
            >
              {isQuickMixing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4" />
              )}
              <span className="ml-1.5 text-xs font-medium">
                {isQuickMixing ? 'Mixing...' : 'Quick Mix'}
              </span>
              {hasProPreferences && (
                <span className="ml-1 text-[9px] bg-purple-500/25 text-purple-300 px-1 py-px rounded-full leading-none">PRO</span>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenQuickMixPro}
              className="border-purple-500/40 bg-purple-500/10 text-purple-400 hover:bg-purple-500/25 hover:text-purple-200 px-1.5 rounded-l-none border-l-0"
              data-testid="quick-mix-pro-settings"
              title="Quick Mix Pro — set your personalized mixing preferences"
            >
              <Settings2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          
          {/* Duplicate Button - Only show if project exists */}
          <DuplicateProjectButton
            projectId={projectId}
            duplicateProject={duplicateProject}
            isSaving={isSaving}
          />
          
          {/* Export Buttons */}
          <ExportButtons
            isExporting={isExporting}
            onExportWav={onExportWav}
            onExportMp3={onExportMp3}
            onOpenBatchExporter={() => setShowBatchExporter(true)}
            canExportMp3={canExportMp3}
          />
            
          {/* User Menu with Logout */}
          <UserMenu
            user={user}
            planId={planId}
            logout={logout}
          />
        </div>
      </div>
    </header>
  );
};

export default HeaderToolbar;
